'use client';

import { MiniPassport } from '@/features/cards/variants/MiniPassport';
import { trackUnlockCtaClick } from '@/features/freemium/components/unlock-cta';
import { getChoosePlanHrefForCreator } from '@/shared/utils/routes';
import type { Creator } from '@/types';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState, type RefObject } from 'react';
import { useCreatorGuestSummary } from './useCreatorGuestSummary';

interface CreatorMiniPassportContainerProps {
  creator: Creator;
  handler: string;
  /** The featured hero block — visibility trigger is scrolling past its midpoint. */
  featuredRef: RefObject<HTMLElement | null>;
  /** The full TicketBand passport — the mini hides while it is in the viewport. */
  passportRef: RefObject<HTMLElement | null>;
}

/**
 * Guest-only sticky mini-passport (spec §4). Owns the behavior contract:
 * hidden until the guest scrolls past ~half the featured block, hidden while
 * the full passport intersects the viewport.
 */
export function CreatorMiniPassportContainer({
  creator,
  handler,
  featuredRef,
  passportRef,
}: CreatorMiniPassportContainerProps) {
  const router = useRouter();
  const guestSummary = useCreatorGuestSummary(creator.id, true);

  const [scrolledPast, setScrolledPast] = useState(false);
  const [passportInView, setPassportInView] = useState(false);

  useEffect(() => {
    const update = () => {
      const el = featuredRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setScrolledPast(rect.top + rect.height * 0.5 < 0);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [featuredRef]);

  useEffect(() => {
    const el = passportRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setPassportInView(entry?.isIntersecting ?? false),
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [passportRef]);

  const handleCta = useCallback(() => {
    const destination = getChoosePlanHrefForCreator(handler);
    trackUnlockCtaClick('mini_passport', destination, handler);
    router.push(destination);
  }, [router, handler]);

  const locked = guestSummary.totalLocked;
  const countries = creator.countries_recorded ?? 0;
  const stops = creator.total_videos ?? creator.videos_count ?? 0;

  const subParts = [
    ...(countries > 0 ? [`${countries} countries`] : []),
    ...(stops > 0 ? [`${stops} stops`] : []),
    'cancel anytime',
  ];

  return (
    <MiniPassport
      visible={scrolledPast && !passportInView}
      {...(locked > 0
        ? {
            titleStrong: `${locked} more`,
            titleRest: `from ${creator.name} — and every creator on Taboo TV`,
          }
        : {
            titleStrong: 'Every exclusive',
            titleRest: `from ${creator.name} — and every creator on Taboo TV`,
          })}
      sub={subParts.join(' · ')}
      ctaLabel="Unlock all"
      onCta={handleCta}
    />
  );
}
